<script setup lang="ts">
import type { CompletionClarity, CompletionConsideration } from '~kui/composables/useCompletionSteps';

import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { useCompletionSteps } from '~kui/composables/useCompletionSteps';
import { useConfetti } from '~kui/composables/useConfetti';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { DEFAULT_ANYWAY_PARTNER_BG_COLOR } from '~kui/constants/anyway';
import { AnimatePresence, motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

import type { FeedbackSurveyQuestion } from './FeedbackSurvey/types';
import type { CompletionActivity } from './KUIUniCompletionProgress.vue';
import type { CompletionSkill } from './KUIUniCompletionSkills.vue';
import type { CompletionPrize, CompletionSpotlight, CompletionSpotlightLinkClick, PrizeOutcome } from './types';

import KUIUniCompletionFeedbackSurvey from './FeedbackSurvey/KUIUniCompletionFeedbackSurvey.vue';
import KUIUniCompletionClarity from './KUIUniCompletionClarity.vue';
import KUIUniCompletionCoins from './KUIUniCompletionCoins.vue';
import KUIUniCompletionConsideration from './KUIUniCompletionConsideration.vue';
import KUIUniCompletionPrize from './KUIUniCompletionPrize.vue';
import KUIUniCompletionProgress from './KUIUniCompletionProgress.vue';
import KUIUniCompletionSkills from './KUIUniCompletionSkills.vue';
import KUIUniCompletionSpotlight from './KUIUniCompletionSpotlight.vue';

export type { FeedbackSurveyQuestion } from './FeedbackSurvey/types';
export type { CompletionPrize, CompletionSpotlight, CompletionSpotlightLinkClick, PrizeOutcome } from './types';

export interface Props {
  background?: string | null;
  badgeUrl?: string | null;
  premiumBadgeUrl?: string | null;
  /** Sponsor disclaimer shown under the badge on the progress step. */
  badgeDisclaimer?: string | null;
  activities?: CompletionActivity[];
  skills?: CompletionSkill[];
  coinsEarned?: number | null;
  bonusCoins?: number | null;
  totalCoins?: number | null;
  prize?: CompletionPrize | null;
  isGhostUser?: boolean;
  progressHeading?: string;
  animateCoins?: boolean;
  partialUnlockMessage?: string | null;
  isReCompletion?: boolean | null;
  prizeOutcome?: PrizeOutcome;
  confirmPrizeLoading?: boolean;
  confirmPrizeError?: string | null;
  ctaLoading?: boolean;
  ctaLabel?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaLoading?: boolean;
  termsTo?: string;
  assetName?: string | null;
  iconPath?: string | null;
  collectionName?: string | null;
  partnerBgColor?: string;
  animationsEnabled?: boolean;
  feedbackSurvey?: FeedbackSurveyQuestion[] | null;
  feedbackSurveySubmitting?: boolean;
  feedbackSurveySharedWithTeacher?: boolean;
  feedbackSurveyAnonymousResponses?: boolean;
  feedbackSurveyErrorMessage?: string | null;
  isFullyCompleted?: boolean;
  considerations?: CompletionConsideration[];
  clarity?: CompletionClarity | null;
  spotlights?: CompletionSpotlight[];
}

const props = withDefaults(defineProps<Props>(), {
  background: '/images/kui/unified/completion-bg-01.webp',
  iconPath: '/images/kui/unified/australia-eng-logo.webp',
  badgeUrl: null,
  premiumBadgeUrl: null,
  badgeDisclaimer: null,
  activities: () => [],
  skills: () => [],
  coinsEarned: null,
  bonusCoins: null,
  totalCoins: null,
  prize: null,
  isGhostUser: false,
  progressHeading: '',
  animateCoins: false,
  partialUnlockMessage: null,
  isReCompletion: null,
  prizeOutcome: null,
  confirmPrizeLoading: false,
  confirmPrizeError: null,
  ctaLoading: false,
  ctaLabel: null,
  secondaryCtaLabel: null,
  secondaryCtaLoading: false,
  assetName: null,
  collectionName: null,
  partnerBgColor: DEFAULT_ANYWAY_PARTNER_BG_COLOR,
  animationsEnabled: true,
  feedbackSurvey: null,
  feedbackSurveySubmitting: false,
  feedbackSurveySharedWithTeacher: false,
  feedbackSurveyAnonymousResponses: false,
  feedbackSurveyErrorMessage: null,
  isFullyCompleted: false,
  considerations: () => [],
  clarity: null,
  spotlights: () => [],
});

const emit = defineEmits<{
  continue: [];
  secondaryCtaClick: [];
  ctaShown: [];
  confirmEntry: [];
  skipPrizeDraw: [];
  cancelPrizeDraw: [];
  feedbackSurveySubmitted: [responses: Record<string, unknown>];
  feedbackSurveySkipped: [answeredCount: number];
  feedbackSurveyAbandoned: [answeredCount: number];
  considerationsViewed: [];
  clarityViewed: [];
  spotlightLinkClick: [click: CompletionSpotlightLinkClick];
}>();

const skillAnswer = defineModel<string>('skillAnswer', { default: '' });

const { t } = useI18n();
const { SLIDE_UP, TRANSITION } = useMotionPresets();
const { triggerConfetti } = useConfetti();

// Keep the static badge when the user has animations disabled in their
// accessibility settings — the 3D badge is inherently motion-driven.
const effectivePremiumBadgeUrl = computed(() =>
  (props.animationsEnabled ? props.premiumBadgeUrl : null),
);

const stepProps = computed(() => ({
  activities: props.activities,
  badgeUrl: props.badgeUrl,
  feedbackSurvey: props.feedbackSurvey,
  isFullyCompleted: props.isFullyCompleted,
  coinsEarned: props.coinsEarned,
  skills: props.skills,
  prize: props.prize,
  assetName: props.assetName,
  collectionName: props.collectionName,
  isGhostUser: props.isGhostUser,
  prizeOutcome: props.prizeOutcome,
  considerations: props.considerations,
  clarity: props.clarity,
  spotlights: props.spotlights,
  isReCompletion: props.isReCompletion,
}));

const {
  activeSteps,
  isEmpty,
  isReCompletion,
  showCta,
  currentStepName,
  showTapOverlay,
  completionHeading,
  ctaText,
  handleTap,
  advanceStep,
  init,
} = useCompletionSteps(stepProps);

const ready = ref(false);
const prizeRef = ref<InstanceType<typeof KUIUniCompletionPrize> | null>(null);

const hasPrizeStep = computed(() => activeSteps.value.includes('prize'));
const showPrizeCta = computed(() => hasPrizeStep.value && showCta.value && props.prizeOutcome === null);

// Fires each time the continue button (and any secondary one) comes on screen: once the steps are
// done and, when there is a prize draw, once the student has entered or skipped it.
watch(() => ready.value && showCta.value && !showPrizeCta.value, (shown) => {
  if (shown) {
    emit('ctaShown');
  }
}, { immediate: true });

onMounted(() => {
  nextTick(() => {
    init();
    ready.value = true;
  });
  if (isEmpty.value) {
    triggerConfetti({ particleCount: 200, spread: 70, origin: { y: 0.4 } });
  }
});

const handleFeedbackSurveySubmitted = (responses: Record<string, unknown>) => {
  emit('feedbackSurveySubmitted', responses);
  advanceStep();
};

// A skip moves the student on exactly like a submit does. Completion was banked before this screen
// rendered, so nothing about their progress hangs on answering.
const handleFeedbackSurveySkipped = (answeredCount: number) => {
  emit('feedbackSurveySkipped', answeredCount);
  advanceStep();
};

const stepClass = 'flex w-full shrink-0 flex-col min-h-full items-center gap-12 md:gap-6 justify-center';
</script>

<template>
  <div class="relative flex h-full flex-col">
    <NuxtImg
      v-if="background"
      :src="background"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 size-full object-cover"
    />
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <div class="absolute left-1/2 top-1/2 size-[140vmax] -translate-x-1/2 -translate-y-1/2">
        <div class="size-full bg-[repeating-conic-gradient(from_0deg,rgba(255,255,255,0.25)_0deg_10deg,transparent_10deg_20deg)]" :class="{ 'animate-[spin_120s_linear_infinite]': animationsEnabled }" />
      </div>
    </div>

    <template v-if="ready">
      <div
        v-if="showTapOverlay"
        role="button"
        tabindex="0"
        :aria-label="t('unified.activity.completion.wellDone')"
        class="absolute inset-0 z-30 cursor-pointer outline-none"
        data-testid="completion-tap-overlay"
        @click="handleTap"
        @keydown.enter="handleTap"
        @keydown.space.prevent="handleTap"
      />

      <!-- Must stay unstacked (no z-index): a stacking context here traps every step's own
           layers below the tap overlay, and the spotlight has to sit above it to be tappable. -->
      <div class="relative flex min-h-0 flex-1 flex-col items-center justify-center-safe overflow-y-auto px-4 sm:px-6">
        <div
          v-if="isEmpty && !isReCompletion"
          class="flex flex-col items-center gap-4 text-center"
        >
          <motion.div v-bind="SLIDE_UP" :transition="TRANSITION">
            <Heading
              size="xl"
              as="h2"
              class="max-w-xs leading-tight"
            >
              {{ completionHeading ?? t('unified.activity.completion.wellDone') }}
            </Heading>
          </motion.div>
          <motion.div
            v-if="!completionHeading"
            v-bind="SLIDE_UP"
            :transition="{ ...TRANSITION, delay: 0.15 }"
          >
            <Text size="lg" class="max-w-xs font-semibold">
              {{ t('unified.activity.completion.greatJob') }}
            </Text>
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            v-if="currentStepName === 'reCompletion' || (isEmpty && isReCompletion)"
            key="reCompletion"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <div class="flex flex-col items-center gap-6 text-center">
              <motion.div v-bind="SLIDE_UP" :transition="TRANSITION">
                <Heading
                  size="lg"
                  as="h3"
                  class="md:max-w-xs leading-none"
                >
                  {{ completionHeading }}
                </Heading>
              </motion.div>
              <motion.div v-bind="SLIDE_UP" :transition="{ ...TRANSITION, delay: 0.15 }">
                <NuxtImg
                  src="/images/kui/unified/thumbs-up.webp"
                  alt=""
                  aria-hidden="true"
                  class="size-64 sm:size-80"
                />
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'consideration'"
            key="consideration"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionConsideration
              :considerations="considerations"
              :animations-enabled="animationsEnabled"
              @viewed="emit('considerationsViewed')"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'clarity'"
            key="clarity"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionClarity
              :before-score="clarity?.beforeScore ?? 0"
              :after-score="clarity?.afterScore ?? 0"
              :illustration="clarity?.illustration"
              :animations-enabled="animationsEnabled"
              @viewed="emit('clarityViewed')"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'coins'"
            key="coins"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionCoins
              :coins-earned="coinsEarned ?? 0"
              :total-coins="totalCoins"
              :coin-effect="animateCoins ? 'burst' : 'none'"
              :is-ghost-user="isGhostUser"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'skills'"
            key="skills"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            class="pb-24"
            :class="[stepClass]"
          >
            <KUIUniCompletionSkills
              :skills="skills"
              :compact="skills.length > 4"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'progress'"
            key="progress"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="[stepClass, { 'pb-24': badgeDisclaimer }]"
          >
            <div class="flex w-full flex-1 flex-col items-center justify-center">
              <KUIUniCompletionProgress
                :heading="completionHeading ?? progressHeading"
                :badge-url="badgeUrl ?? ''"
                :premium-badge-url="effectivePremiumBadgeUrl"
                :icon-path="iconPath ?? undefined"
                :activities="activities"
                :bonus-coins="bonusCoins"
                :animate="animateCoins"
                :partial-unlock-message="partialUnlockMessage"
                :partner-bg-color="partnerBgColor"
                :spotlights="spotlights"
                @spotlight-link-click="emit('spotlightLinkClick', $event)"
              />
            </div>
            <Text
              v-if="badgeDisclaimer"
              size="xs"
              class="max-w-3xl whitespace-pre-line text-center italic"
              data-testid="completion-badge-disclaimer"
            >
              {{ badgeDisclaimer }}
            </Text>
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'spotlight' && spotlights.length"
            key="spotlight"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionSpotlight
              :heading="completionHeading ?? progressHeading"
              :spotlights="spotlights"
              @spotlight-link-click="emit('spotlightLinkClick', $event)"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'feedback'"
            key="feedback"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionFeedbackSurvey
              :questions="feedbackSurvey ?? []"
              :submitting="feedbackSurveySubmitting"
              :shared-with-teacher="feedbackSurveySharedWithTeacher"
              :anonymous-responses="feedbackSurveyAnonymousResponses"
              :error-message="feedbackSurveyErrorMessage"
              @submitted="handleFeedbackSurveySubmitted"
              @skipped="handleFeedbackSurveySkipped"
              @abandoned="emit('feedbackSurveyAbandoned', $event)"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStepName === 'prize'"
            key="prize"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0 }"
            :transition="{ duration: 0.2 }"
            :class="stepClass"
          >
            <KUIUniCompletionPrize
              ref="prizeRef"
              v-model:skill-answer="skillAnswer"
              :title="prize?.title ?? ''"
              :terms-to="termsTo ?? ''"
              :outcome="prizeOutcome"
              :confirm-loading="confirmPrizeLoading"
              :confirm-error="confirmPrizeError"
              :skill-question="prize?.skillQuestion ?? null"
              @confirm-entry="emit('confirmEntry')"
              @cancel-prize-draw="emit('cancelPrizeDraw')"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        v-if="showTapOverlay"
        class="absolute bottom-6 right-4 md:bottom-1/4 md:right-1/5 z-20 animate-in fade-in duration-300 delay-500 fill-mode-both"
      >
        <div class="bg-white rounded-full p-2 animate-tap-pulse">
          <Icon
            size="sm"
            :name="ICONS.tapScreen"
            class="opacity-50"
          />
        </div>
      </div>

      <motion.div
        v-if="showCta"
        class="absolute inset-x-0 bottom-2 md:bottom-6 z-20 flex flex-col items-center gap-3 px-4 sm:px-6"
        :initial="{ opacity: 0, y: 10 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.4, ease: 'easeOut' }"
      >
        <template v-if="showPrizeCta">
          <Button
            variant="secondary"
            size="lg"
            class="max-w-sm text-xl md:text-2xl w-full"
            data-testid="completion-enter-prize-button"
            type="button"
            @click="prizeRef?.openConfirmPrize()"
          >
            {{ t('unified.activity.completion.enterPrizeDraw') }}
          </Button>
          <button
            type="button"
            class="cursor-pointer text-xs underline"
            data-testid="completion-skip-prize-button"
            @click="$emit('skipPrizeDraw')"
          >
            {{ t('unified.activity.completion.skipPrizeDraw') }}
          </button>
        </template>

        <template v-else>
          <Button
            size="lg"
            :loading="ctaLoading"
            :disabled="ctaLoading || secondaryCtaLoading"
            class="max-w-sm text-xl md:text-2xl w-full"
            data-testid="completion-continue-button"
            type="button"
            @click="$emit('continue')"
          >
            {{ ctaLabel ?? ctaText }}
          </Button>
          <Button
            v-if="secondaryCtaLabel"
            variant="link"
            type="button"
            class="h-auto p-0 underline"
            :loading="secondaryCtaLoading"
            :disabled="ctaLoading || secondaryCtaLoading"
            data-testid="completion-secondary-cta-button"
            @click="$emit('secondaryCtaClick')"
          >
            {{ secondaryCtaLabel }}
          </Button>
        </template>
      </motion.div>
    </template>
  </div>
</template>
